// Task materials on the web: the editor used when writing a task, the
// read-only list (with used / returned once the job is done), and the field
// panel showing Accept -> Arrived -> Completed plus any material change
// waiting for a manager or admin. Same rules as the server
// (backend/dwr_materials.py, routes/dwr_field_routes.py).
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { toast } from "sonner";
import { Check, Clock, Hourglass, Loader2, MapPin, Package, Plus, Tag, X } from "lucide-react";
import { clock, minutesLabel } from "@/lib/dwr";
import DeleteButton from "@/components/DeleteButton";

let catalogCache = null;
const loadCatalog = () => {
  if (!catalogCache) catalogCache = api.get("/dwr/materials/catalog").then(({ data }) => data.items).catch((e) => { catalogCache = null; throw e; });
  return catalogCache;
};
let seq = 0;
const newId = () => `n${Date.now().toString(36)}${(seq++).toString(36)}`;
export const fmtQty = (q) => (q === undefined || q === null ? "" : String(Math.round(Number(q) * 1000) / 1000));
const unit = (m) => (m.unit ? ` ${m.unit}` : "");

export function MaterialsEditor({ value, onChange, disabled }) {
  const [catalog, setCatalog] = useState([]);
  useEffect(() => { loadCatalog().then(setCatalog).catch(() => {}); }, []);
  const update = (i, patch) => onChange(value.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  const addProduct = (pid) => {
    const p = catalog.find((x) => x.id === pid);
    if (!p) return;
    const i = value.findIndex((m) => m.product_id === pid);
    if (i >= 0) update(i, { qty: Number(value[i].qty || 0) + 1 });
    else onChange([...value, { id: newId(), product_id: p.id, name: p.name, sku: p.sku, unit: "", qty: 1 }]);
  };
  return (
    <div className="space-y-2" data-testid="task-materials">
      {value.length > 0 && (
        <div className="rounded-lg border border-border divide-y divide-border">
          {value.map((m, i) => (
            <div key={m.id} className="flex items-center gap-2 p-2">
              {m.product_id ? <Package className="w-4 h-4 text-primary shrink-0" /> : <Tag className="w-4 h-4 text-muted-foreground shrink-0" />}
              {m.product_id ? (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{m.name}</p>
                  {m.sku && <p className="text-[11px] text-muted-foreground font-mono">{m.sku}</p>}
                </div>
              ) : (
                <Input className="flex-1 h-8" value={m.name} disabled={disabled} placeholder="Item, e.g. Cat6 cable" onChange={(e) => update(i, { name: e.target.value })} />
              )}
              <Input type="number" min="0" step="any" className="w-20 h-8 text-right tabular-nums" value={m.qty} disabled={disabled}
                onChange={(e) => update(i, { qty: e.target.value === "" ? "" : Number(e.target.value) })} aria-label="Quantity" />
              <Input className="w-16 h-8" value={m.unit || ""} disabled={disabled} placeholder="unit" onChange={(e) => update(i, { unit: e.target.value.slice(0, 20) })} aria-label="Unit" />
              {!disabled && (
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label="Remove">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      {!disabled && (
        <div className="flex flex-wrap gap-2">
          <div className="flex-1 min-w-[220px]">
            <SearchableSelect value="" onValueChange={addProduct} placeholder="Add from inventory…" testId="material-picker"
              options={catalog.map((p) => ({ value: p.id, label: `${p.name}${p.sku ? ` · ${p.sku}` : ""}${p.stock !== undefined && p.stock !== null ? ` · ${p.stock} in stock` : ""}` }))} />
          </div>
          <Button type="button" variant="outline" onClick={() => onChange([...value, { id: newId(), product_id: null, name: "", unit: "", qty: 1 }])}>
            <Plus className="w-4 h-4 mr-1" /> Other item
          </Button>
        </div>
      )}
    </div>
  );
}

export function MaterialsList({ materials }) {
  if (!materials?.length) return <p className="text-sm text-muted-foreground">No materials listed.</p>;
  return (
    <ul className="divide-y divide-border rounded-lg border border-border">
      {materials.map((m) => (
        <li key={m.id} className="flex items-center gap-3 px-3 py-2 text-sm">
          {m.product_id ? <Package className="w-4 h-4 text-muted-foreground" /> : <Tag className="w-4 h-4 text-muted-foreground" />}
          <span className="flex-1">{m.name}{m.sku && <span className="ml-2 text-[11px] text-muted-foreground font-mono">{m.sku}</span>}</span>
          {"used_qty" in m && (
            <span className={`text-xs ${m.returned_qty ? "text-amber-600" : "text-emerald-600"}`}>
              used {fmtQty(m.used_qty)}{m.returned_qty ? ` · ${fmtQty(m.returned_qty)} returned` : ""}
            </span>
          )}
          <span className="tabular-nums font-medium">{fmtQty(m.qty)}{unit(m)}</span>
        </li>
      ))}
    </ul>
  );
}

function RequestDiff({ request }) {
  const key = (m) => m.product_id || m.name.toLowerCase();
  const before = Object.fromEntries((request.current || []).map((m) => [key(m), m]));
  const after = Object.fromEntries((request.proposed || []).map((m) => [key(m), m]));
  const label = (m) => (m ? `${fmtQty(m.qty)}${unit(m)}${"used_qty" in m ? ` (used ${fmtQty(m.used_qty)})` : ""}` : "");
  return (
    <ul className="space-y-1 text-sm">
      {[...new Set([...Object.keys(before), ...Object.keys(after)])].map((k) => {
        const a = before[k]; const b = after[k];
        const kind = !a ? "added" : !b ? "removed" : label(a) !== label(b) ? "changed" : "same";
        const tone = { added: "text-emerald-600", removed: "text-rose-600 line-through", changed: "text-amber-600", same: "text-muted-foreground" }[kind];
        return (
          <li key={k} className="flex justify-between gap-3">
            <span className={tone}>{kind === "added" ? "+ " : kind === "removed" ? "− " : ""}{(b || a).name}</span>
            <span className="tabular-nums">{kind === "changed" ? `${label(a)} → ${label(b)}` : label(b || a)}</span>
          </li>
        );
      })}
    </ul>
  );
}

// A pending material change: what changes, and (for managers/admins) the decision.
export function MaterialRequestCard({ request, onDecided }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(null);
  const decide = async (action) => {
    if (action === "reject" && comment.trim().length < 3) { toast.error("Say why the change is rejected"); return; }
    setBusy(action);
    try {
      await api.post(`/dwr/material-requests/${request.id}/decide`, { action, comment: comment.trim() });
      toast.success(action === "approve" ? "Approved. The task's materials are updated." : "Change rejected");
      onDecided?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-3" data-testid="material-request">
      <div className="flex items-start gap-2">
        <Hourglass className="w-4 h-4 text-amber-600 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium">Material change waiting for approval</p>
          <p className="text-xs text-muted-foreground">{request.task_no} · {request.requested_by_name}{request.note ? `: “${request.note}”` : ""}</p>
        </div>
        <DeleteButton iconOnly endpoint={`/dwr/material-requests/${request.id}`} what="material request" onDeleted={onDecided} />
      </div>
      <RequestDiff request={request} />
      {request.can_decide && (
        <div className="space-y-2">
          <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Comment (required to reject)" />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => decide("reject")} disabled={!!busy}>
              {busy === "reject" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <X className="w-4 h-4 mr-1" />}Reject
            </Button>
            <Button size="sm" onClick={() => decide("approve")} disabled={!!busy} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {busy === "approve" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}Approve
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Accept -> Arrived -> Completed, with the times the server recorded.
export function FieldTimeline({ task }) {
  const accepted = task.accepted_by?.[0];
  const steps = [
    ["Assigned", task.created_at, task.assigned_by_name],
    ["Accepted", accepted?.at, accepted?.name],
    [task.category === "office" ? "Started" : "Arrived", task.arrived_at, task.arrived_by_name],
    ["Completed", task.completed_at, task.completed_by_name],
  ];
  const loc = task.arrived_location;
  return (
    <div className="space-y-2">
      <ol className="grid grid-cols-4 gap-2">
        {steps.map(([label, at, who]) => (
          <li key={label} className={`rounded-lg border p-2 ${at ? "border-emerald-500/40 bg-emerald-500/5" : "border-border"}`}>
            <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
            <p className="text-sm font-semibold tabular-nums">{at ? clock(at) : "—"}</p>
            {at && who && <p className="text-[11px] text-muted-foreground truncate">{who}</p>}
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        {task.duration_minutes !== null && task.duration_minutes !== undefined && (
          <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> On site {minutesLabel(task.duration_minutes)}</span>
        )}
        {loc && (
          <a className="inline-flex items-center gap-1 text-primary" target="_blank" rel="noreferrer"
            href={`https://www.google.com/maps?q=${loc.lat},${loc.lng}`}>
            <MapPin className="w-3.5 h-3.5" /> Arrival location{loc.accuracy ? ` (±${Math.round(loc.accuracy)} m)` : ""}
          </a>
        )}
        {task.completion_note && <span>Result: {task.completion_note}</span>}
      </div>
    </div>
  );
}
